import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spacing } from '../../../../atoms/spacing/spacing';
import { Typography } from '../../../../atoms/typography/typography';
import { TextField } from '../../../../atoms/text-field/text-field';
import { Button } from '../../../../atoms/button/button';
import { Skeleton } from '../../../../atoms/skeleton/skeleton';
import { Checkbox } from '../../../../atoms/checkbox/checkbox';

import {
  SearchWithSuggestions,
  SuggestionItem,
  SuggestionPosition,
} from '../../../../molecules/search-with-suggestions/search-with-suggestions';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../../../../molecules/form/form';
import { z } from 'zod';
import { StyledDiv } from '../../../../common/components/styled-div';
import { StyledFixedButtonWrapper } from '../../../../common/components/styled-fixed-button-wrapper/styled-fixed-button-wrapper';

// Characters to block in number inputs (prevents decimals, scientific notation, and negative signs)
const BLOCKED_NUMBER_INPUT_KEYS = new Set(['.', 'e', 'E', '-', '+']);

export enum observationFieldType {
  number = 'number',
  dropdown = 'dropdown',
  text = 'text',
  phone = 'phone',
  checkbox = 'checkbox',
}

export interface ObservationField {
  isMulti?: boolean;
  name: string;
  type: observationFieldType;
  placeholder: string;
  suggestions?: SuggestionItem[];
  defaultValue?: string | number;
  disabled?: boolean;
  min?: number;
  helperText?: string;
  isLoading?: boolean;
  max?: number;
  step?: number;
}

interface ObservationSetupProps {
  fields: ObservationField[];
  schema: z.ZodObject<z.ZodRawShape>;
  onSubmit?: (data: Record<string, string | number>) => void;
  onFieldChange?: (
    name: string,
    value: string | number,
    setValue: (name: string, value: string | number) => void,
  ) => void;
  readonly?: boolean;
  scheduleInfo?: string;
  isSelf?: boolean;
}

export const ObservationSetup: FC<ObservationSetupProps> = ({
  fields,
  schema,
  onSubmit,
  onFieldChange,
  readonly = false,
  scheduleInfo,
  isSelf = false,
}) => {
  // clean up fields by filtering out invalid suggestions
  const cleanedFields = fields.map((field) => {
    if (field.type === observationFieldType.dropdown && field.suggestions) {
      const validSuggestions = field.suggestions.filter((suggestion) => {
        const isValid =
          suggestion &&
          typeof suggestion.id === 'string' &&
          typeof suggestion.label === 'string' &&
          suggestion.label !== null &&
          suggestion.label.trim() !== '';

        return isValid;
      });

      return {
        ...field,
        suggestions: validSuggestions,
      };
    }
    return field;
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: cleanedFields.reduce(
      (acc, field) => {
        acc[field.name] = field.defaultValue ?? '';
        return acc;
      },
      {} as Record<string, string | number>,
    ),
  });

  /**
   * Maps internal observation field types to standard HTML input types.
   * This prevents numeric spinners from appearing on text fields and
   * ensures the correct keyboard is shown on mobile devices.
   */
  const mapFieldTypeToInputType = (type: observationFieldType) => {
    switch (type) {
      case observationFieldType.number:
        return 'number' as const;
      case observationFieldType.phone:
        return 'tel' as const;
      default:
        return 'text' as const;
    }
  };

  const handleSubmit = (data: Record<string, string | number>) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const renderFieldInput = (field: ObservationField, formField: any) => {
    if (field.isLoading) {
      return <Skeleton height="56px" width="100%" />;
    }

    if (field.type === observationFieldType.checkbox) {
      return (
        <Checkbox
          label={field.placeholder}
          checked={!!formField.value}
          onChange={(isChecked) => {
            formField.onChange(isChecked);
            onFieldChange?.(field.name, isChecked ? 1 : 0, (name, value) => {
              form.setValue(name, value);
            });
          }}
          disabled={field.disabled}
          stateLess={true}
          defaultChecked={!!field.defaultValue}
        />
      );
    }

    if (field.type === observationFieldType.dropdown) {
      return (
        <SearchWithSuggestions
          value={
            formField.value
              ? field.suggestions?.find((s) => s.id === formField.value)
                  ?.label || ''
              : ''
          }
          suggestions={field.suggestions || []}
          placeholder={field.placeholder}
          onChange={(item) => {
            const val = item ? item.id : '';
            formField.onChange(val);
            onFieldChange?.(field.name, val, (name, value) => {
              form.setValue(name, value);
            });
          }}
          showDropdownOnFocus={true}
          disabled={field.disabled}
          data-cy={`observation-setup-${field.name}-dropdown`}
        />
      );
    }

    return (
      <TextField
        type={mapFieldTypeToInputType(field.type)}
        placeholder={field.placeholder}
        value={formField.value}
        min={field.min?.toString()}
        onKeyPress={(e) => {
          if (
            field.type === observationFieldType.number &&
            BLOCKED_NUMBER_INPUT_KEYS.has(e.key)
          ) {
            e.preventDefault();
          }
        }}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value;
          /**
           * Only parse as integer if the field is explicitly marked as a number type.
           * This preserves string formatting (like leading zeros or special characters)
           * for phone numbers and general text.
           */
          const processedValue =
            field.type === observationFieldType.number && value
              ? parseInt(value, 10)
              : value;
          formField.onChange(processedValue);
          onFieldChange?.(field.name, processedValue, (name, val) => {
            form.setValue(name, val);
          });
        }}
        disabled={field.disabled}
        data-cy={`observation-setup-${field.name}-input`}
      />
    );
  };

  return (
    <>
      <Spacing marginTop="xl">
        <Typography variant="textBodyMediumNeutral10" textAlign="center">
          Section A
        </Typography>
      </Spacing>
      <Spacing marginTop="sm">
        <Typography variant="textTitleMedium" textAlign="center">
          {isSelf ? 'Class Insights Setup' : 'Observation Setup'}
        </Typography>
      </Spacing>
      <Spacing margin="md none">
        <Typography variant="textBodyLargeNeutral10">
          {isSelf
            ? 'Complete the following information to record your lesson and generate class insights.'
            : 'Complete the following information to begin observation'}
        </Typography>
      </Spacing>

      <Spacing marginTop="lg">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
            noValidate
          >
            {scheduleInfo && (
              <Spacing margin="xl">
                <Typography variant="textBodyLargeNeutral10" textAlign="center">
                  {scheduleInfo}
                </Typography>
              </Spacing>
            )}
            <StyledDiv flexDirection="column" gap={16} marginBottom={64}>
              {cleanedFields.map((field) => (
                <FormField
                  key={field.name}
                  control={form.control}
                  name={field.name}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        {renderFieldInput(field, formField)}
                      </FormControl>
                      {field.helperText && (
                        <Spacing marginTop="xs">
                          <Typography variant="textLabelSmallSecondary">
                            {field.helperText}
                          </Typography>
                        </Spacing>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </StyledDiv>

            <StyledFixedButtonWrapper bottom={0}>
              <Button type="submit" data-cy="start-observation-button">
                {readonly
                  ? 'View Observation'
                  : isSelf
                    ? 'Next'
                    : 'Start Observation'}
              </Button>
            </StyledFixedButtonWrapper>
          </form>
        </Form>
      </Spacing>
    </>
  );
};
